import {constructUsing, createMap, forMember, mapFrom, type Mapper, mapWith} from '@automapper/core';
import type {IMapping} from '@igp/shared/auto-mapper';
import type {PlayerEcologyWeeklyReport} from '@igp/shared/contract/pam';
import type {IsNever} from '@igp/shared/utils';

import {PlayerEcologySummaryModelTypeName, PlayerEcologySummaryViewModelTypeName} from '../player-level-report-v2/constants';

import {
    PlayerEcologyWeeklyColumnsTypeName,
    PlayerEcologyWeeklyQueryFieldsTypeName,
    PlayerEcologyWeeklyReportTypeName,
    PlayerEcologyWeeklySortKeysTypeName,
    PlayerEcologyWeeklyViewModelKeysTypeName,
    PlayerEcologyWeeklyViewModelTypeName,
} from './constants';
import type {
    PlayerEcologyWeeklyColumns,
    PlayerEcologyWeeklyQueryFields,
    PlayerEcologyWeeklySortKeys,
    PlayerEcologyWeeklyViewModel,
    PlayerEcologyWeeklyViewModelKeys,
} from './types';

export class PlayerEcologyWeeklyMapping implements IMapping {
    createMapping(mapper: Mapper): void {
        createMap<PlayerEcologyWeeklyViewModelKeys, PlayerEcologyWeeklyQueryFields>(
            mapper,
            PlayerEcologyWeeklyViewModelKeysTypeName,
            PlayerEcologyWeeklyQueryFieldsTypeName,
            constructUsing(key => {
                const mapping: Record<PlayerEcologyWeeklyViewModelKeys, PlayerEcologyWeeklyQueryFields> = {
                    rowId: 'row_id',
                    playerUid: 'player_uid',
                    playerUsername: 'player_username',
                    agentUid: 'agent_uid',
                    agentUsername: 'agent_username',
                    country: 'country',
                    'reportWeekStart.seconds': 'report_week_start.seconds',
                    'ecologySummary.ecology_flag': 'ecology_summary.ecology_flag',
                    'ecologySummary.is_pro_shortdeck': 'ecology_summary.is_pro_shortdeck',
                    'ecologySummary.is_pro_zoom': 'ecology_summary.is_pro_zoom',
                    'ecologySummary.stake_2_is_pro_nlhe': 'ecology_summary.stake_2_is_pro_nlhe',
                    'ecologySummary.stake_4_is_pro_nlhe': 'ecology_summary.stake_4_is_pro_nlhe',
                    'ecologySummary.stake_10_is_pro_nlhe': 'ecology_summary.stake_10_is_pro_nlhe',
                    'ecologySummary.stake_20_is_pro_nlhe': 'ecology_summary.stake_20_is_pro_nlhe',
                    'ecologySummary.stake_50_is_pro_nlhe': 'ecology_summary.stake_50_is_pro_nlhe',
                    'ecologySummary.stake_100_is_pro_nlhe': 'ecology_summary.stake_100_is_pro_nlhe',
                    'ecologySummary.stake_200_is_pro_nlhe': 'ecology_summary.stake_200_is_pro_nlhe',
                    'ecologySummary.stake_400_is_pro_nlhe': 'ecology_summary.stake_400_is_pro_nlhe',
                };
                return mapping[key];
            })
        );
        createMap<PlayerEcologyWeeklySortKeys, PlayerEcologyWeeklyViewModelKeys>(
            mapper,
            PlayerEcologyWeeklySortKeysTypeName,
            PlayerEcologyWeeklyViewModelKeysTypeName,
            constructUsing(sortKey => {
                const mapping: Record<PlayerEcologyWeeklySortKeys, PlayerEcologyWeeklyViewModelKeys> = {
                    row_id: 'rowId',
                    player_uid: 'playerUid',
                    player_username: 'playerUsername',
                    agent_uid: 'agentUid',
                    agent_username: 'agentUsername',
                    country: 'country',
                    report_week_start: 'reportWeekStart.seconds',
                    'ecology_summary.ecology_flag': 'ecologySummary.ecology_flag',
                    'ecology_summary.is_pro_shortdeck': 'ecologySummary.is_pro_shortdeck',
                    'ecology_summary.is_pro_zoom': 'ecologySummary.is_pro_zoom',
                    'ecology_summary.stake_2_is_pro_nlhe': 'ecologySummary.stake_2_is_pro_nlhe',
                    'ecology_summary.stake_4_is_pro_nlhe': 'ecologySummary.stake_4_is_pro_nlhe',
                    'ecology_summary.stake_10_is_pro_nlhe': 'ecologySummary.stake_10_is_pro_nlhe',
                    'ecology_summary.stake_20_is_pro_nlhe': 'ecologySummary.stake_20_is_pro_nlhe',
                    'ecology_summary.stake_50_is_pro_nlhe': 'ecologySummary.stake_50_is_pro_nlhe',
                    'ecology_summary.stake_100_is_pro_nlhe': 'ecologySummary.stake_100_is_pro_nlhe',
                    'ecology_summary.stake_200_is_pro_nlhe': 'ecologySummary.stake_200_is_pro_nlhe',
                    'ecology_summary.stake_400_is_pro_nlhe': 'ecologySummary.stake_400_is_pro_nlhe',
                };
                return mapping[sortKey];
            })
        );
        createMap<PlayerEcologyWeeklyViewModelKeys, PlayerEcologyWeeklySortKeys>(
            mapper,
            PlayerEcologyWeeklyViewModelKeysTypeName,
            PlayerEcologyWeeklySortKeysTypeName,
            constructUsing(vmKey => {
                const mapping: Record<PlayerEcologyWeeklyViewModelKeys, PlayerEcologyWeeklySortKeys> = {
                    rowId: 'row_id',
                    playerUid: 'player_uid',
                    playerUsername: 'player_username',
                    agentUid: 'agent_uid',
                    agentUsername: 'agent_username',
                    country: 'country',
                    'reportWeekStart.seconds': 'report_week_start',
                    'ecologySummary.ecology_flag': 'ecology_summary.ecology_flag',
                    'ecologySummary.is_pro_shortdeck': 'ecology_summary.is_pro_shortdeck',
                    'ecologySummary.is_pro_zoom': 'ecology_summary.is_pro_zoom',
                    'ecologySummary.stake_2_is_pro_nlhe': 'ecology_summary.stake_2_is_pro_nlhe',
                    'ecologySummary.stake_4_is_pro_nlhe': 'ecology_summary.stake_4_is_pro_nlhe',
                    'ecologySummary.stake_10_is_pro_nlhe': 'ecology_summary.stake_10_is_pro_nlhe',
                    'ecologySummary.stake_20_is_pro_nlhe': 'ecology_summary.stake_20_is_pro_nlhe',
                    'ecologySummary.stake_50_is_pro_nlhe': 'ecology_summary.stake_50_is_pro_nlhe',
                    'ecologySummary.stake_100_is_pro_nlhe': 'ecology_summary.stake_100_is_pro_nlhe',
                    'ecologySummary.stake_200_is_pro_nlhe': 'ecology_summary.stake_200_is_pro_nlhe',
                    'ecologySummary.stake_400_is_pro_nlhe': 'ecology_summary.stake_400_is_pro_nlhe',
                };
                return mapping[vmKey];
            })
        );
        createMap<PlayerEcologyWeeklyReport, PlayerEcologyWeeklyViewModel>(
            mapper,
            PlayerEcologyWeeklyReportTypeName,
            PlayerEcologyWeeklyViewModelTypeName,
            forMember(
                vm => vm.agentUid,
                mapFrom(m => m.agent_uid)
            ),
            forMember(
                vm => vm.agentUsername,
                mapFrom(m => m.agent_username)
            ),
            forMember(
                vm => vm.country,
                mapFrom(m => m.country)
            ),
            forMember(
                vm => vm.playerUid,
                mapFrom(m => m.player_uid)
            ),
            forMember(
                vm => vm.playerUsername,
                mapFrom(m => m.player_username)
            ),
            forMember(
                vm => vm.reportWeekStart,
                mapFrom(m => m.report_week_start)
            ),
            forMember(
                vm => vm.rowId,
                mapFrom(m => m.row_id)
            ),
            forMember(
                vm => vm.ecologySummary,
                mapWith(PlayerEcologySummaryViewModelTypeName, PlayerEcologySummaryModelTypeName, m => m.ecology_summary)
            )
        );
        createMap<PlayerEcologyWeeklyViewModelKeys, PlayerEcologyWeeklyColumns>(
            mapper,
            PlayerEcologyWeeklyViewModelKeysTypeName,
            PlayerEcologyWeeklyColumnsTypeName,
            constructUsing(vmKey => {
                const nonColumnKeys: PlayerEcologyWeeklyViewModelKeys[] = ['rowId'];
                type FieldsToMap = Exclude<PlayerEcologyWeeklyViewModelKeys, PlayerEcologyWeeklyColumns | (typeof nonColumnKeys)[number]>;
                const isNever: IsNever<FieldsToMap> = true;
                if (!isNever) {
                    throw new Error('Mapping should be defined for all values');
                }
                return nonColumnKeys.includes(vmKey) ? undefined : vmKey;
            })
        );
        createMap<PlayerEcologyWeeklyColumns, PlayerEcologyWeeklyViewModelKeys>(
            mapper,
            PlayerEcologyWeeklyColumnsTypeName,
            PlayerEcologyWeeklyViewModelKeysTypeName,
            constructUsing(vmKey => {
                type FieldsToMap = Exclude<PlayerEcologyWeeklyColumns, PlayerEcologyWeeklyViewModelKeys>;
                const isNever: IsNever<FieldsToMap> = true;
                if (!isNever) {
                    throw new Error('Mapping should be defined for all values');
                }
                return vmKey;
            })
        );
    }
}
